import React, { useEffect, useState } from 'react';
import { subscriptionService } from '../services/tenantService';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

function SubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const { register, handleSubmit, reset, setValue } = useForm();

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const data = await subscriptionService.getAllPlans();
      setPlans(data);
    } catch (error) {
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      // Convert features string to array
      const planData = {
        ...data,
        features: data.features ? data.features.split('\n').filter(f => f.trim()) : [],
      };

      if (editingPlan) {
        await subscriptionService.updatePlan(editingPlan.id, planData);
        toast.success('Plan updated successfully!');
      } else {
        await subscriptionService.createPlan(planData);
        toast.success('Plan created successfully!');
      }
      
      setShowModal(false);
      setEditingPlan(null);
      reset();
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save plan');
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setValue('name', plan.name);
    setValue('billing_cycle', plan.billing_cycle);
    setValue('price', plan.price);
    setValue('max_doctors', plan.max_doctors);
    setValue('max_staff', plan.max_staff);
    setValue('max_appointments_per_day', plan.max_appointments_per_day);
    setValue('features', plan.features ? plan.features.join('\n') : '');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this plan?')) {
      try {
        await subscriptionService.deletePlan(id);
        toast.success('Plan deactivated successfully');
        loadPlans();
      } catch (error) {
        toast.error('Failed to deactivate plan');
      }
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Subscription Plans</h1>
        <button
          onClick={() => {
            setEditingPlan(null);
            reset();
            setShowModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold"
        >
          + Add New Plan
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div key={plan.id} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-800">{plan.name}</h3>
                <span className={`inline-block mt-2 px-3 py-1 text-xs rounded-full ${
                  plan.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {plan.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {plan.billing_cycle}
              </span>
            </div>

            <div className="mb-6">
              <span className="text-4xl font-bold text-blue-600">₹{plan.price}</span>
              <span className="text-gray-600">/{plan.billing_cycle === 'monthly' ? 'month' : 'year'}</span>
            </div>

            <div className="space-y-2 mb-6">
              <div className="flex items-center text-sm">
                <span className="text-gray-600">Max Doctors:</span>
                <span className="ml-auto font-semibold">{plan.max_doctors}</span>
              </div>
              <div className="flex items-center text-sm">
                <span className="text-gray-600">Max Staff:</span>
                <span className="ml-auto font-semibold">{plan.max_staff}</span>
              </div>
              <div className="flex items-center text-sm">
                <span className="text-gray-600">Max Appointments/Day:</span>
                <span className="ml-auto font-semibold">{plan.max_appointments_per_day}</span>
              </div>
            </div>

            {plan.features && plan.features.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-sm mb-2">Features:</h4>
                <ul className="space-y-1">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(plan)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-semibold"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(plan.id)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg text-sm font-semibold"
              >
                Deactivate
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Plan Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">
              {editingPlan ? 'Edit Plan' : 'Add New Plan'}
            </h2>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Plan Name *</label>
                <input
                  {...register('name', { required: true })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Monthly Plan"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Billing Cycle *</label>
                <select
                  {...register('billing_cycle', { required: true })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select Cycle</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Price (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  {...register('price', { required: true })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="1200.00"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Max Doctors *</label>
                  <input
                    type="number"
                    {...register('max_doctors', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="5"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Max Staff *</label>
                  <input
                    type="number"
                    {...register('max_staff', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="10"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Max Appts/Day *</label>
                  <input
                    type="number"
                    {...register('max_appointments_per_day', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="50"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  Features (one per line)
                </label>
                <textarea
                  {...register('features')}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="5"
                  placeholder="Basic appointment management&#10;Patient records&#10;Email support"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold"
                >
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPlan(null);
                    reset();
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionPlans;
